package com.karyadina.ui.screens.studyPlanner.components

import androidx.compose.foundation.layout.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import com.karyadina.model.StudySession
import com.karyadina.model.Subject
import com.karyadina.service.FlashcardReviewNeed
import com.karyadina.ui.theme.StudyStrings
import com.karyadina.util.currentTimeMillis
import org.jetbrains.compose.resources.stringResource

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun AddSessionDialog(
    subjects: List<Subject>,
    flashcardSubject: List<FlashcardReviewNeed>,
    onDismiss: () -> Unit,
    onAdd: (StudySession) -> Unit
) {
    var subject by remember { mutableStateOf(subjects.firstOrNull()?.name ?: "") }
    var topic by remember { mutableStateOf("") }
    var duration by remember { mutableStateOf("60") }

    var expandedSubject by remember { mutableStateOf(false) }
    var expandedTopic by remember { mutableStateOf(false) }

    // Store the selected Flashcard object to access its Units
    var selectedFlashcardSubject by remember { mutableStateOf<FlashcardReviewNeed?>(null) }

    AlertDialog(
        onDismissRequest = onDismiss,
        title = { Text(stringResource(StudyStrings.PlannerDialogAddSessionTitle)) },
        text = {
            Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {

                // 1. Subject Dropdown
                if (subjects.isNotEmpty() || flashcardSubject.isNotEmpty()) {
                    ExposedDropdownMenuBox(expanded = expandedSubject, onExpandedChange = { expandedSubject = it }) {
                        OutlinedTextField(
                            value = subject,
                            onValueChange = {},
                            label = { Text(stringResource(StudyStrings.PlannerLabelSubject)) },
                            modifier = Modifier.fillMaxWidth().menuAnchor(),
                            readOnly = true,
                            trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(expandedSubject) }
                        )
                        ExposedDropdownMenu(expanded = expandedSubject, onDismissRequest = { expandedSubject = false }) {
                            // Regular Subjects
                            subjects.forEach { s ->
                                DropdownMenuItem(
                                    text = { Text(s.name) },
                                    onClick = {
                                        subject = s.name
                                        //selectedFlashcardSubject = null // Reset flashcard selection
                                        expandedSubject = false
                                    }
                                )
                            }
                            // Flashcard Subjects
                            flashcardSubject.forEach { f ->
                                DropdownMenuItem(
                                    text = { Text("${f.subjectName} (Flashcards)") },
                                    onClick = {
                                        subject = f.subjectName
                                        selectedFlashcardSubject = f // Store selection
                                        expandedSubject = false
                                    }
                                )
                            }
                        }
                    }
                } else {
                    OutlinedTextField(
                        value = subject,
                        onValueChange = { subject = it },
                        label = { Text(stringResource(StudyStrings.PlannerLabelSubject)) },
                        modifier = Modifier.fillMaxWidth()
                    )
                }

                // 2. Topic Field (Switches to Dropdown if Flashcard Subject Selected)
                if (selectedFlashcardSubject != null && selectedFlashcardSubject!!.Units.isNotEmpty()) {
                    ExposedDropdownMenuBox(expanded = expandedTopic, onExpandedChange = { expandedTopic = it }) {
                        OutlinedTextField(
                            value = topic,
                            onValueChange = {},
                            label = { Text(stringResource(StudyStrings.PlannerLabelTopicUnit)) },
                            modifier = Modifier.fillMaxWidth().menuAnchor(),
                            readOnly = true,
                            trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(expandedTopic) }
                        )
                        ExposedDropdownMenu(expanded = expandedTopic, onDismissRequest = { expandedTopic = false }) {
                            // Assuming FlashcardReviewNeed has a list called 'Units'
                            selectedFlashcardSubject!!.Units.forEach { unit ->
                                DropdownMenuItem(
                                    text = { Text(unit.name) },
                                    onClick = {
                                        topic = unit.name
                                        expandedTopic = false
                                    }
                                )
                            }
                        }
                    }
                } else {
                    OutlinedTextField(value = topic, onValueChange = { topic = it }, label = { Text(stringResource(StudyStrings.PlannerLabelTopic)) }, modifier = Modifier.fillMaxWidth())
                }

                OutlinedTextField(value = duration, onValueChange = { duration = it }, label = { Text(stringResource(StudyStrings.PlannerLabelDuration)) }, modifier = Modifier.fillMaxWidth())
            }
        },
        confirmButton = {
            Button(
                onClick = {
                    onAdd(StudySession(
                        id = "", subject = subject, topic = topic,
                        startTime = currentTimeMillis(),
                        endTime = currentTimeMillis() + (duration.toLongOrNull() ?: 60) * 60 * 1000,
                        durationMinutes = duration.toIntOrNull() ?: 60
                    ))
                },
                enabled = subject.isNotBlank()
            ) { Text(stringResource(StudyStrings.Addbtn)) }
        },
        dismissButton = { TextButton(onClick = onDismiss) { Text(stringResource(StudyStrings.Cancel)) } }
    )
}

@Composable
fun AddSubjectDialog(onDismiss: () -> Unit, onAdd: (Subject) -> Unit) {
    var name by remember { mutableStateOf("") }
    var code by remember { mutableStateOf("") }
    var targetHours by remember { mutableStateOf("") }

    AlertDialog(
        onDismissRequest = onDismiss,
        title = { Text(stringResource(StudyStrings.PlannerDialogAddSubjectTitle)) },
        text = {
            Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
                OutlinedTextField(value = name, onValueChange = { name = it }, label = { Text(stringResource(StudyStrings.PlannerLabelName)) }, modifier = Modifier.fillMaxWidth())
                OutlinedTextField(value = code, onValueChange = { code = it }, label = { Text(stringResource(StudyStrings.PlannerLabelCode)) }, modifier = Modifier.fillMaxWidth())
                OutlinedTextField(value = targetHours, onValueChange = { targetHours = it }, label = { Text(stringResource(StudyStrings.PlannerLabelHours)) }, modifier = Modifier.fillMaxWidth())
            }
        },
        confirmButton = {
            Button(onClick = {
                onAdd(Subject(id = "", name = name, code = code, targetHoursPerWeek = targetHours.toFloatOrNull() ?: 0f))
            }, enabled = name.isNotBlank()) { Text(stringResource(StudyStrings.Addbtn)) }
        },
        dismissButton = { TextButton(onClick = onDismiss) { Text(stringResource(StudyStrings.Cancel)) } }
    )
}